// Redaction for request errors before they reach the console (STN-427).
// Only the reschedule-link credential is covered: `rt` (GET rescheduleLink
// query, and inside the `path` param) and `rescheduleToken`
// (bookAppointmentFlow body). Kept out of
// apiService.js so it can be unit-tested without importing Store.js.
const REDACTED = "[redacted]";

const redactUrl = (url) =>
  typeof url === "string" && /[?&]rt=/.test(url)
    ? url.replace(/([?&]rt=)[^&#]*/g, `$1${REDACTED}`)
    : url;

// `path` is the window.location.href apiService adds to every request. `rt`
// is stripped from the address bar on arrival, so it shouldn't be there;
// redacted anyway in case a request goes out first.
const redactParams = (params) => {
  if (!params || typeof params !== "object") return params;
  const path = redactUrl(params.path);
  if (!("rt" in params) && path === params.path) return params;
  return {
    ...params,
    ...("rt" in params && { rt: REDACTED }),
    ...(path !== params.path && { path }),
  };
};

// axios has already serialised an object body to a JSON string by the time
// it fails, so the string form is the one that usually turns up here.
const redactData = (data) => {
  if (typeof data === "string") {
    return data.replace(
      /("rescheduleToken"\s*:\s*)"(?:[^"\\]|\\.)*"/g,
      `$1"${REDACTED}"`,
    );
  }
  return data && typeof data === "object" && "rescheduleToken" in data
    ? { ...data, rescheduleToken: REDACTED }
    : data;
};

export const redactRequestConfig = (config) => {
  if (!config || typeof config !== "object") return config;
  const params = redactParams(config.params);
  const url = redactUrl(config.url);
  const data = redactData(config.data);
  if (params === config.params && url === config.url && data === config.data) {
    return config;
  }
  return { ...config, params, url, data };
};

// Returns the error itself when there is nothing to redact. Otherwise returns
// a copy with the configs redacted and the raw `request` objects dropped (an
// XHR's `responseURL` carries the full query string). Never mutates `error`.
export const redactErrorForLog = (error) => {
  if (!error || typeof error !== "object") return error;
  const config = redactRequestConfig(error.config);
  const responseConfig = redactRequestConfig(error.response?.config);
  if (config === error.config && responseConfig === error.response?.config) {
    return error;
  }
  const copy = Object.assign(Object.create(Object.getPrototypeOf(error)), error, {
    message: error.message,
    stack: error.stack,
    config,
    request: undefined,
  });
  if (error.response) {
    copy.response = { ...error.response, config: responseConfig, request: undefined };
  }
  return copy;
};
